/**
 * Background Picker Component
 *
 * Composant pour sélectionner un background prédéfini ou uploader un custom
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image,
  Upload,
  Check,
  Loader2,
  X,
  Palette,
  Grid3X3,
  Square,
  Trash2,
} from 'lucide-react';
import { profileApi, PredefinedBackground, BackgroundType } from '../api/profile.api';
import { logger } from '@/utils/logger';

export interface BackgroundPickerProps {
  /** Background actuel de l'utilisateur */
  currentBackground: string | null;
  /** Type de background actuel */
  currentBackgroundType: BackgroundType;
  /** Callback quand le background change */
  onBackgroundChange: (backgroundType: BackgroundType, background: string | null) => void;
  /** Afficher en mode compact */
  compact?: boolean;
  /** Classe CSS additionnelle */
  className?: string;
}

type CategoryTab = 'gradients' | 'patterns' | 'solids' | 'custom';

export const BackgroundPicker: React.FC<BackgroundPickerProps> = ({
  currentBackground,
  currentBackgroundType,
  onBackgroundChange,
  compact = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [backgrounds, setBackgrounds] = useState<Record<string, PredefinedBackground>>({});
  const [byCategory, setByCategory] = useState<{
    gradients: PredefinedBackground[];
    patterns: PredefinedBackground[];
    solids: PredefinedBackground[];
  }>({ gradients: [], patterns: [], solids: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<CategoryTab>('gradients');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger les backgrounds prédéfinis
  useEffect(() => {
    const loadBackgrounds = async () => {
      try {
        const data = await profileApi.getAvailableBackgrounds();
        setBackgrounds(data.backgrounds);
        setByCategory(data.byCategory);
        logger.debug('Backgrounds loaded:', data.total);
      } catch (err) {
        logger.error('Failed to load backgrounds:', err);
        setError(t('auth.backgroundPicker.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadBackgrounds();
  }, []);

  // Sélectionner un background prédéfini
  const handleSelectPredefined = async (bg: PredefinedBackground) => {
    try {
      setError(null);
      await profileApi.updateBackground({
        backgroundType: 'PREDEFINED',
        background: bg.id,
      });
      onBackgroundChange('PREDEFINED', bg.id);
    } catch (err) {
      logger.error('Failed to update background:', err);
      setError(t('auth.backgroundPicker.updateError'));
    }
  };

  // Upload un background personnalisé
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation côté client
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('auth.backgroundPicker.unsupportedFormat'));
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError(t('auth.backgroundPicker.fileTooLarge'));
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const result = await profileApi.uploadBackground(file);
      if (result && result.background) {
        onBackgroundChange('CUSTOM', result.background);
        logger.info('Background uploaded successfully');
      } else if (result && result.upload?.url) {
        // Fallback si background n'est pas dans result mais upload.url l'est
        onBackgroundChange('CUSTOM', result.upload.url);
        logger.info('Background uploaded successfully (via upload.url)');
      } else {
        throw new Error(t('auth.backgroundPicker.serverError'));
      }
    } catch (err) {
      logger.error('Failed to upload background:', err);
      setError(t('auth.backgroundPicker.uploadError'));
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer le background
  const handleRemove = async () => {
    try {
      setError(null);
      await profileApi.deleteBackground();
      onBackgroundChange('NONE', null);
    } catch (err) {
      logger.error('Failed to remove background:', err);
      setError(t('auth.backgroundPicker.deleteError'));
    }
  };

  // Obtenir le style CSS pour un background prédéfini
  const getBackgroundStyle = (bg: PredefinedBackground): React.CSSProperties => {
    if (bg.type === 'solid') {
      return { backgroundColor: bg.value };
    }
    return {
      background: bg.value,
      backgroundSize: bg.backgroundSize || 'cover',
    };
  };

  // Vérifier si un background est sélectionné
  const isSelected = (bgId: string) => {
    return currentBackgroundType === 'PREDEFINED' && currentBackground === bgId;
  };

  const tabs: { id: CategoryTab; label: string; icon: React.ReactNode }[] = [
    { id: 'gradients', label: t('auth.backgroundPicker.gradients'), icon: <Palette className="w-4 h-4" /> },
    { id: 'patterns', label: t('auth.backgroundPicker.patterns'), icon: <Grid3X3 className="w-4 h-4" /> },
    { id: 'solids', label: t('auth.backgroundPicker.colors'), icon: <Square className="w-4 h-4" /> },
    { id: 'custom', label: t('auth.backgroundPicker.custom'), icon: <Image className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
              transition-all duration-200 whitespace-nowrap
              ${activeTab === tab.id
                ? 'bg-teal-600 text-white shadow-lg'
                : 'bg-white/50 text-gray-700 hover:bg-white/80'
              }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          <X className="w-4 h-4" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Current Background Preview */}
      {currentBackgroundType !== 'NONE' && (
        <div className="relative">
          <p className="text-xs text-gray-500 mb-2">{t('auth.backgroundPicker.current')}</p>
          <div className="relative h-20 rounded-xl overflow-hidden border-2 border-teal-500">
            {currentBackgroundType === 'PREDEFINED' && currentBackground && backgrounds[currentBackground] ? (
              <div
                className="absolute inset-0"
                style={getBackgroundStyle(backgrounds[currentBackground])}
              />
            ) : currentBackgroundType === 'CUSTOM' && currentBackground ? (
              <img
                src={currentBackground}
                alt="Background personnalisé"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg
                hover:bg-red-600 transition-colors shadow-lg"
              title={t('auth.backgroundPicker.deleteTitle')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'custom' ? (
        <div className="space-y-4">
          {/* Upload Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer
              transition-all duration-200
              ${uploading
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/50'
              }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />

            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
                <p className="text-sm text-teal-600 font-medium">{t('auth.backgroundPicker.uploading')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-teal-100 rounded-2xl">
                  <Upload className="w-8 h-8 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t('auth.backgroundPicker.uploadImage')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('auth.backgroundPicker.uploadHint')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Custom background preview if exists */}
          {currentBackgroundType === 'CUSTOM' && currentBackground && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-700">
                  {t('auth.backgroundPicker.customActive')}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Predefined Backgrounds Grid */
        <div className={`grid gap-3 ${compact ? 'grid-cols-4' : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'}`}>
          {byCategory[activeTab as keyof typeof byCategory]?.map((bg) => (
            <button
              key={bg.id}
              onClick={() => handleSelectPredefined(bg)}
              className={`relative aspect-video rounded-xl overflow-hidden transition-all duration-200
                ${isSelected(bg.id)
                  ? 'ring-2 ring-teal-500 ring-offset-2 scale-105'
                  : 'hover:ring-2 hover:ring-teal-300 hover:scale-102'
                }`}
              title={bg.name}
            >
              <div
                className="absolute inset-0"
                style={getBackgroundStyle(bg)}
              />
              {isSelected(bg.id) && (
                <div className="absolute inset-0 flex items-center justify-center bg-teal-500/30">
                  <div className="p-1.5 bg-white rounded-full shadow-lg">
                    <Check className="w-4 h-4 text-teal-600" />
                  </div>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/50 to-transparent">
                <p className="text-[10px] text-white font-medium truncate">
                  {bg.name}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Reset to default option */}
      {currentBackgroundType !== 'NONE' && (
        <button
          onClick={handleRemove}
          className="w-full flex items-center justify-center gap-2 p-3
            bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl
            transition-colors text-sm font-medium"
        >
          <X className="w-4 h-4" />
          {t('auth.backgroundPicker.useDefault')}
        </button>
      )}
    </div>
  );
};

export default BackgroundPicker;
